/* Businesscases (/crm/businesscases): één compacte, doorzoekbare lijst, zoals de
 * projectlijst (ADR 0023).
 *
 * Tabs: actief, leeg of onvolledig, gearchiveerd, alle. Per case: project, naam met
 * organisatie en jaren, netto investering, terugverdientijd, ROI jaar 1 (uitgerekend,
 * niet opgeslagen) en wanneer bijgewerkt. Naast de lijst een vergelijkweergave. */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArchiveRestore, BarChart3, Copy, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { datum, euro } from '@/features/projectpagina/ui/opmaak';
import { LijstStaat, SorteerKop, Tabs, useLijstToetsen, Voet, Werkbalk } from '@/features/relaties/ui/Lijst';
import { Pil } from '@/features/relaties/ui/Onderdelen';
import { Archiveer } from '@/features/relaties/ui/Archiveer';
import {
  beginRichting, businesscaseLijst, filterEnSorteer, filterNaarUrl, tellingen, TAB_LABEL,
  type LijstBusinesscase, type LijstFilter, type Sortering, type Weergave,
} from './data/lijst';
import { jaar, procent } from './data/opmaak';

const KOLOMMEN =
  'grid-cols-[48px_minmax(0,1fr)] sm:grid-cols-[52px_minmax(0,1fr)_112px] md:grid-cols-[52px_minmax(0,1fr)_112px_96px] lg:grid-cols-[52px_minmax(0,1fr)_112px_96px_84px] xl:grid-cols-[52px_minmax(0,1fr)_120px_104px_88px_80px]';

interface Props {
  cases: LijstBusinesscase[];
  laden: boolean;
  fout: Error | null;
  filter: LijstFilter;
  onFilter: (f: LijstFilter) => void;
  weergave: Weergave;
  onWeergave: (w: Weergave) => void;
  magBeheren: boolean;
  onNieuw: () => void;
  onDupliceer: (b: LijstBusinesscase) => void;
  onArchiveer: (b: LijstBusinesscase) => void;
  onHerstel: (b: LijstBusinesscase) => void;
  vergelijk: (cases: LijstBusinesscase[]) => ReactNode;
}

export function Signaal({ b }: { b: Pick<LijstBusinesscase, 'leeg' | 'onvolledig' | 'cijfers' | 'gearchiveerd' | 'project'> }) {
  if (b.gearchiveerd) return <Pil className="border-zinc-200 bg-zinc-100 text-zinc-600">Gearchiveerd</Pil>;
  if (b.leeg) return <Pil className="border-amber-200 bg-amber-50 text-amber-800">Leeg</Pil>;
  if (b.onvolledig) {
    return (
      <Pil className="border-amber-200 bg-amber-50 text-amber-800">
        {b.cijfers.investering > 0 ? 'Geen opbrengsten' : 'Geen kosten'}
      </Pil>
    );
  }
  if (b.project?.verwijderd) return <Pil className="border-zinc-200 bg-zinc-100 text-zinc-600">Project gearchiveerd</Pil>;
  return null;
}

export function BusinesscaseLijst(props: Props) {
  const { cases, laden, fout, filter, onFilter } = props;
  const navigate = useNavigate();
  const zoekRef = useRef<HTMLInputElement>(null);
  const rijRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [actief, setActief] = useState(-1);

  const zichtbaar = useMemo(() => filterEnSorteer(cases, filter), [cases, filter]);
  const tabs = useMemo(() => tellingen(cases, filter.zoek).map((t) => ({ ...t, label: TAB_LABEL[t.tab] })), [cases, filter.zoek]);

  useEffect(() => setActief(-1), [filter.zoek, filter.tab]);
  useEffect(() => {
    if (actief >= 0) rijRefs.current[actief]?.scrollIntoView({ block: 'nearest' });
  }, [actief]);
  useEffect(() => {
    if (!laden) businesscaseLijst.bewaar(new URLSearchParams(filterNaarUrl(filter)).toString(), zichtbaar.map((b) => b.id));
  }, [zichtbaar, laden, filter]);

  const open = useCallback((i: number) => {
    const b = zichtbaar[i];
    if (b) navigate(`/crm/businesscases/${b.id}`);
  }, [zichtbaar, navigate]);
  const wis = useCallback(() => onFilter({ ...filter, zoek: '' }), [filter, onFilter]);
  useLijstToetsen({ zoekRef, aantal: props.weergave === 'lijst' ? zichtbaar.length : 0, actief, setActief, open, wis });

  const sorteer = (s: Sortering) =>
    onFilter({ ...filter, sortering: s, oplopend: filter.sortering === s ? !filter.oplopend : beginRichting(s) });
  const kop = { huidig: filter.sortering, oplopend: filter.oplopend, onSorteer: sorteer };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <Werkbalk
            ref={zoekRef}
            titel="Businesscases"
            zoek={filter.zoek}
            onZoek={(zoek) => onFilter({ ...filter, zoek })}
            placeholder="Zoek case, project of organisatie"
            nieuw={props.magBeheren ? { label: 'Nieuwe businesscase', onKlik: props.onNieuw } : null}
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs tabs={tabs} label="Stand" actief={filter.tab} onKies={(tab) => onFilter({ ...filter, tab })}
          stip={{ onvolledig: 'bg-amber-500', gearchiveerd: 'bg-zinc-400' }} />
        <div role="group" aria-label="Weergave" className="flex rounded-lg border p-0.5">
          {([['lijst', List, 'Lijst'], ['vergelijken', BarChart3, 'Vergelijken']] as const).map(([w, Icoon, label]) => (
            <button
              key={w}
              type="button"
              aria-pressed={props.weergave === w}
              onClick={() => props.onWeergave(w)}
              className={cn('flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm', props.weergave === w ? 'bg-muted font-medium' : 'text-muted-foreground hover:text-foreground')}
            >
              <Icoon className="h-4 w-4" aria-hidden />
              <span className="hidden md:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {fout || laden || zichtbaar.length === 0 ? (
        <LijstStaat
          fout={fout}
          laden={laden}
          wat="Businesscases"
          leeg={
            <>
              {cases.length === 0 ? 'Nog geen businesscases.' : 'Geen businesscases gevonden.'}{' '}
              {(filter.zoek || filter.tab !== 'actief') && cases.length > 0 && (
                <button type="button" className="font-medium text-foreground underline" onClick={() => onFilter({ ...filter, zoek: '', tab: 'alle' })}>
                  Toon alles
                </button>
              )}
            </>
          }
        />
      ) : props.weergave === 'vergelijken' ? (
        <div data-weergave="vergelijken">{props.vergelijk(zichtbaar)}</div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-background">
          <div className="flex items-center border-b bg-muted/40 text-xs text-muted-foreground">
            <div className={cn('grid flex-1 items-center gap-3 py-2 pl-4 pr-2', KOLOMMEN)}>
              <SorteerKop s="project" {...kop}>Project</SorteerKop>
              <SorteerKop s="naam" {...kop}>Businesscase</SorteerKop>
              <span className="hidden text-right sm:block"><SorteerKop s="investering" {...kop}>Netto investering</SorteerKop></span>
              <span className="hidden text-right md:block"><SorteerKop s="terugverdientijd" {...kop}>Terugverdiend</SorteerKop></span>
              <span className="hidden text-right lg:block">ROI jaar 1</span>
              <span className="hidden xl:block"><SorteerKop s="bijgewerkt" {...kop}>Bijgewerkt</SorteerKop></span>
            </div>
            {props.magBeheren && <span className="w-[76px] shrink-0" />}
          </div>
          <ul className="divide-y">
            {zichtbaar.map((b, i) => {
              const meta = [
                b.project?.organisatie && b.project.organisatie !== b.project.naam ? b.project.organisatie : b.project?.naam,
                b.cijfers.eersteJaar ? `${b.cijfers.eersteJaar}–${b.cijfers.laatsteJaar}` : null,
                b.modus === 'netto' ? 'netto ROI' : null,
              ].filter(Boolean);
              return (
                <li key={b.id} data-bc-rij={b.id} className={cn('group flex items-center', i === actief && 'bg-muted', b.gearchiveerd && 'text-muted-foreground')}>
                  <Link
                    ref={(el) => (rijRefs.current[i] = el)}
                    to={`/crm/businesscases/${b.id}`}
                    onMouseEnter={() => setActief(i)}
                    aria-label={`${b.naam} openen`}
                    className={cn('grid flex-1 items-center gap-3 py-2.5 pl-4 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', KOLOMMEN)}
                  >
                    <span className="font-mono text-xs text-muted-foreground">{b.project?.nummer ?? '—'}</span>
                    <span className="flex min-w-0 flex-col">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium">{b.naam}</span>
                        <Signaal b={b} />
                      </span>
                      {meta.length > 0 && <span className="truncate text-xs text-muted-foreground">{meta.join(' · ')}</span>}
                    </span>
                    <span className="hidden text-right tabular-nums sm:block">{b.cijfers.investering > 0 ? euro(b.cijfers.nettoInvestering) : '—'}</span>
                    <span className="hidden text-right tabular-nums md:block">{jaar(b.cijfers.terugverdientijd)}</span>
                    <span className="hidden text-right tabular-nums lg:block">{procent(b.cijfers.roiJaar1)}</span>
                    <span className="hidden text-muted-foreground xl:block" title={b.bijgewerkt ?? b.aangemaakt}>{datum(b.bijgewerkt ?? b.aangemaakt)}</span>
                  </Link>
                  {props.magBeheren && (
                    <span className="flex w-[76px] shrink-0 items-center justify-end gap-0.5 pr-2 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                      <Button variant="ghost" size="icon" aria-label="Dupliceren" title="Dupliceren" className="h-8 w-8 text-muted-foreground" onClick={() => props.onDupliceer(b)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      {b.gearchiveerd ? (
                        <Button variant="ghost" size="icon" aria-label="Terugzetten" title="Terugzetten" className="h-8 w-8 text-muted-foreground" onClick={() => props.onHerstel(b)}>
                          <ArchiveRestore className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Archiveer wat="businesscase" naam={b.naam} bezwaar={null} onArchiveer={() => props.onArchiveer(b)}
                          terugzetten="onder de tab Gearchiveerd" uitleg="De cijfers blijven bewaard." />
                      )}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <Voet aantal={zichtbaar.length} enkelvoud="businesscase" meervoud="businesscases" />
        </div>
      )}
    </div>
  );
}
